import { useState, useEffect } from 'react';
import { Modal, Table, Badge, Spinner, Form, Row, Col, Button, Pagination } from 'react-bootstrap';
import ModalHelp from '../../../components/ModalHelp';
import { toast } from 'react-toastify';
import ContaServicoViewModel from '../../../viewmodel/ContaServicoViewModel';

interface LogsAcessoModalProps {
    show: boolean;
    conta: any;
    onClose: () => void;
}

export default function LogsAcessoModal({ show, conta, onClose }: LogsAcessoModalProps) {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    useEffect(() => {
        if (show && conta) {
            carregarLogs();
        }
    }, [show, conta, page]);

    const carregarLogs = async () => {
        setLoading(true);
        try {
            const response = await ContaServicoViewModel.getLogsAcesso(conta.clientId, page, 20, dataInicio, dataFim);
            setLogs(response.content);
            setTotalPages(response.totalPages);
        } catch (error) {
            toast.error('Erro ao carregar logs de acesso');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: number) => {
        if (status >= 200 && status < 300) return 'success';
        if (status >= 400 && status < 500) return 'warning';
        if (status >= 500) return 'danger';
        return 'secondary';
    };

    return (
        <Modal show={show} onHide={onClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Logs de Acesso</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ModalHelp text="Consulte o histórico de acessos da conta de serviço. Filtre por data, método HTTP e status da resposta para investigar o uso da conta." />
                <Row className="mb-3">
                    <Col md={5}>
                        <Form.Group>
                            <Form.Label>Data Início</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5}>
                        <Form.Group>
                            <Form.Label>Data Fim</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                        <Button className="w-100" onClick={carregarLogs}>
                            Filtrar
                        </Button>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : logs.length === 0 ? (
                    <p className="text-muted text-center py-5">Nenhum log encontrado</p>
                ) : (
                    <>
                        <Table striped bordered hover responsive size="sm">
                            <thead>
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Método</th>
                                    <th>Endpoint</th>
                                    <th>Status</th>
                                    <th>IP Origem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.id}>
                                        <td>
                                            <small>
                                                {new Date(log.dataAcesso).toLocaleString('pt-BR')}
                                            </small>
                                        </td>
                                        <td>
                                            <Badge bg="secondary">{log.metodoHttp}</Badge>
                                        </td>
                                        <td>
                                            <small className="font-monospace">{log.endpoint}</small>
                                        </td>
                                        <td>
                                            <Badge bg={getStatusColor(log.statusResposta)}>
                                                {log.statusResposta}
                                            </Badge>
                                        </td>
                                        <td>
                                            <small className="text-muted">{log.ipOrigem || 'N/A'}</small>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {totalPages > 1 && (
                            <Pagination>
                                <Pagination.First onClick={() => setPage(0)} disabled={page === 0} />
                                <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 0} />
                                {[...Array(Math.min(totalPages, 5))].map((_, index) => (
                                    <Pagination.Item
                                        key={index}
                                        active={index === page}
                                        onClick={() => setPage(index)}
                                    >
                                        {index + 1}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages - 1} />
                                <Pagination.Last onClick={() => setPage(totalPages - 1)} disabled={page === totalPages - 1} />
                            </Pagination>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
